/**
 * MFA Enrollment Component
 * Handles TOTP authenticator app setup with QR code display
 */

import { useState, useEffect } from 'react';
import QRCode from 'qrcode';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from '@/components/ui/input-otp';
import {
  Loader2,
  Shield,
  Smartphone,
  Copy,
  Check,
  AlertCircle,
  Key,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface MFAEnrollmentProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

type EnrollmentStep = 'intro' | 'qr-code' | 'verify' | 'recovery' | 'complete';

interface Factor {
  id: string;
  type: string;
  totp: {
    qr_code: string;
    secret: string;
    uri: string;
  };
}

export function MFAEnrollment({ onSuccess, onCancel }: MFAEnrollmentProps) {
  const [step, setStep] = useState<EnrollmentStep>('intro');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [factor, setFactor] = useState<Factor | null>(null);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const { toast } = useToast();

  // Generate recovery codes (client-side for display, Supabase handles actual storage)
  const generateRecoveryCodes = (): string[] => {
    const codes: string[] = [];
    for (let i = 0; i < 10; i++) {
      const code = Array.from({ length: 10 }, () =>
        'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'[Math.floor(Math.random() * 36)]
      ).join('');
      codes.push(`${code.slice(0, 5)}-${code.slice(5)}`);
    }
    return codes;
  };

  // Start MFA enrollment
  const startEnrollment = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App'
      });

      if (error) {
        throw error;
      }

      if (data) {
        setFactor(data as Factor);

        // Generate QR code from the TOTP URI
        const qrDataUrl = await QRCode.toDataURL(data.totp.uri, {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        });
        setQrCodeDataUrl(qrDataUrl);
        setStep('qr-code');
      }
    } catch (err: any) {
      console.error('MFA enrollment error:', err);
      setError(err.message || 'Failed to start MFA enrollment');
    } finally {
      setIsLoading(false);
    }
  };

  // Verify the TOTP code and complete enrollment
  const verifyAndComplete = async () => {
    if (verificationCode.length !== 6 || !factor) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: factor.id,
        code: verificationCode
      });

      if (error) {
        throw error;
      }

      // Generate recovery codes to show user
      const codes = generateRecoveryCodes();
      setRecoveryCodes(codes);

      // Store hashed recovery codes in database
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/store-recovery-codes`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session?.access_token}`,
            },
            body: JSON.stringify({ recoveryCodes: codes }),
          }
        );

        if (!response.ok) {
          console.error('Failed to store recovery codes');
        }
      } catch (err) {
        console.error('Error storing recovery codes:', err);
      }

      setStep('recovery');

      toast({
        title: 'MFA Verified',
        description: 'Two-factor authentication has been enabled.',
      });
    } catch (err: any) {
      console.error('MFA verification error:', err);
      setError(err.message || 'Invalid verification code. Please try again.');
      setVerificationCode('');
    } finally {
      setIsLoading(false);
    }
  };

  // Copy secret to clipboard
  const copySecret = async () => {
    if (factor?.totp.secret) {
      await navigator.clipboard.writeText(factor.totp.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
      toast({
        title: 'Copied',
        description: 'Secret key copied to clipboard',
      });
    }
  };

  // Copy recovery codes to clipboard
  const copyRecoveryCodes = async () => {
    const codesText = recoveryCodes.join('\n');
    await navigator.clipboard.writeText(codesText);
    toast({
      title: 'Copied',
      description: 'Recovery codes copied to clipboard',
    });
  };

  // Render based on current step
  const renderStep = () => {
    switch (step) {
      case 'intro':
        return (
          <div className="space-y-6">
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="w-10 h-10 text-primary" />
              </div>
            </div>

            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">Add Extra Security</h3>
              <p className="text-sm text-muted-foreground">
                Two-factor authentication adds an extra layer of security to your account by requiring a code from your phone in addition to your password.
              </p>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Smartphone className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Use any authenticator app</p>
                  <p className="text-xs text-muted-foreground">
                    Google Authenticator, Authy, 1Password, or any TOTP app
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Key className="w-5 h-5 text-primary mt-0.5" />
                <div>
                  <p className="text-sm font-medium">Get recovery codes</p>
                  <p className="text-xs text-muted-foreground">
                    Backup codes in case you lose access to your authenticator
                  </p>
                </div>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3">
              {onCancel && (
                <Button variant="outline" className="flex-1" onClick={onCancel}>
                  Cancel
                </Button>
              )}
              <Button
                className="flex-1"
                onClick={startEnrollment}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        );

      case 'qr-code':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">Scan QR Code</h3>
              <p className="text-sm text-muted-foreground">
                Open your authenticator app and scan this QR code to add your account.
              </p>
            </div>

            {qrCodeDataUrl && (
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-lg shadow-inner border">
                  <img
                    src={qrCodeDataUrl}
                    alt="MFA QR Code"
                    className="w-48 h-48"
                  />
                </div>
              </div>
            )}

            {/* Manual entry option */}
            <div className="space-y-2">
              <p className="text-xs text-center text-muted-foreground">
                Can't scan? Enter this key manually:
              </p>
              <div className="flex items-center justify-center gap-2">
                <code className="px-3 py-2 bg-muted rounded text-sm font-mono">
                  {factor?.totp.secret}
                </code>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copySecret}
                  className="h-8 w-8 p-0"
                >
                  {secretCopied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setStep('intro')}>
                Back
              </Button>
              <Button className="flex-1" onClick={() => setStep('verify')}>
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        );

      case 'verify':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <h3 className="text-lg font-semibold">Verify Setup</h3>
              <p className="text-sm text-muted-foreground">
                Enter the 6-digit code from your authenticator app to verify setup.
              </p>
            </div>

            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={verificationCode}
                onChange={setVerificationCode}
                disabled={isLoading}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setStep('qr-code')}>
                Back
              </Button>
              <Button
                className="flex-1"
                onClick={verifyAndComplete}
                disabled={isLoading || verificationCode.length !== 6}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>
            </div>
          </div>
        );

      case 'recovery':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <h3 className="text-lg font-semibold">Save Recovery Codes</h3>
              <p className="text-sm text-muted-foreground">
                Save these codes in a secure place. You can use them to access your account if you lose your authenticator device.
              </p>
            </div>

            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Each code can only be used once. Store them securely.
              </AlertDescription>
            </Alert>

            <div className="bg-muted rounded-lg p-4">
              <div className="grid grid-cols-2 gap-2">
                {recoveryCodes.map((code, index) => (
                  <code key={index} className="text-sm font-mono p-2 bg-background rounded text-center">
                    {code}
                  </code>
                ))}
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full"
              onClick={copyRecoveryCodes}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy All Codes
            </Button>

            <Button className="w-full" onClick={() => setStep('complete')}>
              I've Saved My Codes
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        );

      case 'complete':
        return (
          <div className="space-y-6">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                  <Shield className="w-10 h-10 text-green-600" />
                </div>
              </div>
              <h3 className="text-xl font-semibold">Two-Factor Authentication Enabled</h3>
              <p className="text-sm text-muted-foreground">
                Your account is now protected with an additional layer of security. You'll need to enter a code from your authenticator app each time you sign in.
              </p>
            </div>

            <Button className="w-full" onClick={onSuccess}>
              Done
            </Button>
          </div>
        );
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary" />
          Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          Protect your account with an authenticator app
        </CardDescription>
      </CardHeader>
      <CardContent>
        {renderStep()}
      </CardContent>
    </Card>
  );
}

export default MFAEnrollment;
